import { ref, computed } from 'vue';
import { useStorage } from './useStorage';
import { Template, ValidationResult, TemplateMetadata, QueryOptions } from '@/types/supabase';
import { logger, LogCategory } from '@/services/logger';
import { TemplateStorageService } from '@/features/template-storage/services/templateStorage';
import { validateExcelTemplate } from '@/features/template-storage/utils/templateValidator';

/**
 * Composable for template storage operations
 */
export function useTemplateStorage() {
  useStorage();
  const templateService = new TemplateStorageService();
  
  const loading = ref(false);
  const error = ref<Error | null>(null);
  // Use any type to avoid TypeScript errors
  const templates = ref<any[]>([]);
  const selectedTemplate = ref<Template | null>(null);
  
  // Initialize the template storage service
  (async () => {
    try {
      await templateService.initialize();
      logger.info('Template storage service initialized successfully', LogCategory.STORAGE);
    } catch (err) {
      logger.error('Failed to initialize template storage service', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
    }
  })();

  // Computed properties for filtering templates
  const pendingTemplates = computed<Template[]>(() => {
    const result: Template[] = [];
    for (const t of templates.value) {
      if (t.status === 'pending') {
        result.push(t);
      }
    }
    return result;
  });
  
  const approvedTemplates = computed<Template[]>(() => {
    const result: Template[] = [];
    for (const t of templates.value) {
      if (t.status === 'approved') {
        result.push(t);
      }
    }
    return result;
  });
  
  const rejectedTemplates = computed<Template[]>(() => {
    const result: Template[] = [];
    for (const t of templates.value) {
      if (t.status === 'rejected') {
        result.push(t);
      }
    }
    return result;
  });
  
  /**
   * Upload a template file
   * @param file The template file to upload
   * @param metadata Additional metadata for the template
   * @returns The created template
   */
  async function uploadTemplate(file: File, metadata: Partial<TemplateMetadata>) {
    loading.value = true;
    error.value = null;
    
    try {
      // Ensure required fields are present
      if (!metadata.name) {
        throw new Error('Template name is required');
      }
      
      if (!metadata.user_id) {
        throw new Error('User ID is required');
      }
      
      const template = await templateService.uploadTemplate(
        file,
        {
          name: metadata.name,
          description: metadata.description,
          user_id: metadata.user_id,
          is_ai_generated: metadata.is_ai_generated
        }
      );
      
      // Add to local state without using spread operator
      const newTemplates: Template[] = [template];
      for (const t of templates.value) {
        newTemplates.push(t);
      }
      templates.value = newTemplates;
      
      return template;
    } catch (err) {
      logger.error('Error uploading template', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Validate a template file without uploading it
   * @param file The template file to validate
   * @returns Validation result
   */
  async function validateTemplate(file: File): Promise<ValidationResult> {
    loading.value = true;
    error.value = null;
    
    try {
      return await validateExcelTemplate(file);
    } catch (err) {
      logger.error('Error validating template', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Get templates
   * @param options Query options
   * @returns Array of templates
   */
  async function getTemplates(options?: QueryOptions) {
    loading.value = true;
    error.value = null;
    
    try {
      const result = await templateService.getTemplates(options);
      templates.value = result;
      return result;
    } catch (err) {
      logger.error('Error fetching templates', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Get the latest template
   * @returns The latest template or null if none found
   */
  async function getLatestTemplate() {
    loading.value = true;
    error.value = null;
    
    try {
      const template = await templateService.getLatestTemplate();
      if (template) {
        selectedTemplate.value = template;
      }
      return template;
    } catch (err) {
      logger.error('Error fetching latest template', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Get a template by ID
   * @param templateId The template ID
   * @returns The template or null if not found
   */
  async function getTemplateById(templateId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      const template = await templateService.getTemplateById(templateId);
      if (template) {
        selectedTemplate.value = template;
      }
      return template;
    } catch (err) {
      logger.error('Error fetching template by ID', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Download a template
   * @param templateId The template ID
   * @returns The template file as a Blob
   */
  async function downloadTemplate(templateId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      return await templateService.downloadTemplate(templateId);
    } catch (err) {
      logger.error('Error downloading template', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Update a template's metadata
   * @param templateId The template ID
   * @param updates The updates to apply
   * @returns The updated template
   */
  async function updateTemplate(templateId: string, updates: Partial<Template>) {
    loading.value = true;
    error.value = null;
    
    try {
      const template = await templateService.updateTemplate(templateId, updates);
      
      // Update in local state
      const index = templates.value.findIndex(t => t.id === templateId);
      if (index !== -1) {
        templates.value[index] = template;
      }
      
      if (selectedTemplate.value?.id === templateId) {
        selectedTemplate.value = template;
      }
      
      return template;
    } catch (err) {
      logger.error('Error updating template', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Delete a template
   * @param templateId The template ID
   * @returns True if successful
   */
  async function deleteTemplate(templateId: string) {
    loading.value = true;
    error.value = null;
    
    try {
      const result = await templateService.deleteTemplate(templateId);
      
      // Remove from local state without using filter
      const newTemplates: Template[] = [];
      for (const t of templates.value) {
        if (t.id !== templateId) {
          newTemplates.push(t);
        }
      }
      templates.value = newTemplates;
      
      if (selectedTemplate.value?.id === templateId) {
        selectedTemplate.value = null;
      }
      
      return result;
    } catch (err) {
      logger.error('Error deleting template', LogCategory.STORAGE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Update template status
   * @param templateId The template ID
   * @param status The new status
   * @returns The updated template
   */
  async function updateTemplateStatus(templateId: string, status: 'pending' | 'approved' | 'rejected') {
    return updateTemplate(templateId, { status });
  }
  
  return {
    loading,
    error,
    templates,
    selectedTemplate,
    pendingTemplates,
    approvedTemplates,
    rejectedTemplates,
    uploadTemplate,
    validateTemplate,
    getTemplates,
    getLatestTemplate,
    getTemplateById,
    downloadTemplate,
    updateTemplate,
    deleteTemplate,
    updateTemplateStatus
  };
}
